import React, { useEffect, useState, useMemo } from "react";
import { PageHeader, Card, Tag, fmt } from "../components/UI";
import DataTable from "../components/DataTable";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { useNavigate } from "react-router-dom";
import { Filter, X } from "lucide-react";
import { TimeFilter, periodRange } from "../components/TimeFilter";
import api from "../lib/api";

const PROGRAM_OPTIONS = [
  { value: "", label: "All programs" },
  { value: "Q1-FY26", label: "Quarter 1" },
  { value: "Q2-FY26", label: "Quarter 2" },
  { value: "Q3-FY26", label: "Quarter 3" },
  { value: "Q4-FY26", label: "Quarter 4" },
  { value: "ANNUAL-FY26", label: "Annual" },
];

export default function Hierarchy() {
  const [d, setD] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [states, setStates] = useState([]);
  const [dses, setDses] = useState([]);
  const [filters, setFilters] = useState({ program_id: "", state: "", region: "", dse: "" });
  const [preset, setPreset] = useState("this_month");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const nav = useNavigate();

  const range = useMemo(() => periodRange(preset, customStart, customEnd), [preset, customStart, customEnd]);

  // Resolve filter dropdowns
  useEffect(() => { (async () => {
    try {
      const [pgRes, phRes, usrRes] = await Promise.all([
        api.get("/programs"), api.get("/physios?limit=500"), api.get("/users"),
      ]);
      setPrograms(pgRes.data || []);
      const stSet = new Set((phRes.data || []).map((p) => p.state).filter(Boolean));
      setStates([...stSet].sort());
      setDses((usrRes.data || []).filter((u) => u.role === "dse"));
    } catch (_) { /* allow page to still render */ }
  })(); }, []);

  // Reload analytics whenever filters or period change
  useEffect(() => {
    (async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      if (range.start) params.append("start_date", range.start);
      if (range.end) params.append("end_date", range.end);
      const { data } = await api.get(`/analytics/territory${params.toString() ? "?" + params : ""}`);
      setD(data);
    })();
  }, [filters, range.start, range.end]);

  const activeFilterCount = useMemo(() => Object.values(filters).filter(Boolean).length, [filters]);
  const setF = (k, v) => setFilters({ ...filters, [k]: v });
  const clearAll = () => setFilters({ program_id: "", state: "", region: "", dse: "" });

  const programOptions = programs.length > 0
    ? [{ value: "", label: "All programs" }, ...programs.map((p) => ({ value: p.id, label: p.name }))]
    : PROGRAM_OPTIONS;

  if (!d) return <div className="text-slate-400 text-sm">Loading…</div>;

  const cityCols = [
    { key: "city", label: "City", filter: "text", render: (r) => <span className="font-medium">{r.city}</span> },
    { key: "physios", label: "Physio", filter: "range", align: "right", render: (r) => fmt(r.physios) },
    { key: "active_points", label: "Active points", filter: "range", align: "right", render: (r) => fmt(r.active_points) },
    { key: "dse_count", label: "DSEs", filter: "range", align: "right", render: (r) => fmt(r.dse_count) },
  ];

  const dseCols = [
    { key: "name", label: "DSE", filter: "text", render: (r) => <span className="font-medium">{r.name}</span> },
    { key: "territory", label: "Territory", filter: "text", render: (r) => r.territory || "—" },
    { key: "active_physios", label: "Active physio", filter: "range", align: "right", render: (r) => fmt(r.active_physios) },
    { key: "inactive_physios", label: "Inactive physio", filter: "range", align: "right", render: (r) => fmt(r.inactive_physios) },
    { key: "orders_placed", label: "Orders placed", filter: "range", align: "right", render: (r) => fmt(r.orders_placed) },
    { key: "order_value", label: "Order value (₹)", filter: "range", align: "right", render: (r) => fmt(r.order_value) },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        kicker="Network"
        title="Territory & Hierarchy"
        subtitle="A unified view across National → Zonal → Regional → DSE. Click any city to drill into the physios it covers."
        testId="hierarchy-page"
      />

      <TimeFilter
        preset={preset} setPreset={setPreset}
        customStart={customStart} setCustomStart={setCustomStart}
        customEnd={customEnd} setCustomEnd={setCustomEnd}
        testId="territory-time-filter"
      />

      {/* Filter bar */}
      <Card title="Filters" subtitle={activeFilterCount > 0 ? `${activeFilterCount} active` : "Pick any combination — results refresh live"}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="territory-filters">
          <div>
            <label className="small-caps text-slate-500">Program</label>
            <select value={filters.program_id} onChange={(e) => setF("program_id", e.target.value)} className="w-full text-[12.5px]" data-testid="filter-program">
              {programOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="small-caps text-slate-500">State</label>
            <select value={filters.state} onChange={(e) => setF("state", e.target.value)} className="w-full text-[12.5px]" data-testid="filter-state">
              <option value="">All states</option>
              {states.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="small-caps text-slate-500">Region</label>
            <input value={filters.region} onChange={(e) => setF("region", e.target.value)} placeholder="e.g. West, South" className="w-full text-[12.5px]" data-testid="filter-region" />
          </div>
          <div>
            <label className="small-caps text-slate-500">DSE</label>
            <select value={filters.dse} onChange={(e) => setF("dse", e.target.value)} className="w-full text-[12.5px]" data-testid="filter-dse">
              <option value="">All DSEs</option>
              {dses.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
        </div>
        {activeFilterCount > 0 && (
          <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
            <div className="text-[11px] text-slate-500 flex items-center gap-2"><Filter size={11} /> Filters applied: {activeFilterCount}</div>
            <button onClick={clearAll} className="text-[11px] text-slate-500 hover:text-slate-800 flex items-center gap-1" data-testid="filter-clear"><X size={11} /> Clear all</button>
          </div>
        )}
      </Card>

      <Card title="City performance" subtitle="Top 10 cities by active points">
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={d.city_performance}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="city" tick={{ fontSize: 11, fill: "#475569" }} />
              <YAxis tick={{ fontSize: 11, fill: "#475569" }} />
              <Tooltip contentStyle={{ fontSize: 12 }} />
              <Bar dataKey="active_points" fill="#0F766E" radius={[4, 4, 0, 0]} cursor="pointer" onClick={(e) => nav(`/physios?city=${e.city}`)} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card title="City rankings" subtitle="Click a row to drill into physios in that city">
        <DataTable columns={cityCols} data={d.city_performance} rowKey="city" onRowClick={(r) => nav(`/physios?city=${encodeURIComponent(r.city)}`)} searchableKeys={["city"]} exportFilename="cbc-cities" testIdPrefix="cities" initialSort={{ key: "active_points", dir: "desc" }} paginated pageSize={25} />
      </Card>

      <Card title="DSE performance leaderboard" subtitle="Active/Inactive physios, orders placed & order value per DSE">
        <DataTable columns={dseCols} data={d.dse_performance} rowKey="id" searchableKeys={["name", "territory"]} exportFilename="cbc-dse" testIdPrefix="dse" initialSort={{ key: "order_value", dir: "desc" }} paginated pageSize={25} />
      </Card>
    </div>
  );
}
